/**
 * LeadDetailPanel — right-side drawer on the My Leads / All Leads page.
 *
 * Reuses the EXISTING lead object + the existing `/api/notes?leadId=…`
 * endpoint. Zero new backend endpoints, zero schema changes — this is
 * a presentation layer over data that was already in the pipeline. The
 * visual language mirrors `GrokLeadDetailPanel` so the two sibling pages
 * feel like one app.
 *
 * Sections (top → bottom):
 *   1. Header — avatar + name + status pill + "Assigned to … • last
 *      activity N min ago" + X close.
 *   2. Primary actions — Call Borrower (big black, tel: link into the
 *      existing dialer), Add Note (opens the existing note modal via a
 *      global custom event the Leads page listens to), "…" overflow
 *      and "Open full profile" that navigates to the existing
 *      `/leads/:id` route.
 *   3. Contact Information — phone/email/address with copy icons.
 *   4. Loan Details — 2-column grid of the application fields we
 *      already store (purpose, type, amount, rate, payment, value, LTV,
 *      occupancy, program).
 *   5. Follow-ups & Appointments — pulls from the existing
 *      `appointmentAt` / `followUpAt` fields.
 *   6. Notes — last 3 notes from `/api/notes?leadId=…`; "View all"
 *      navigates to the full profile.
 *   7. Documents — a stub for now (surface anything already on the
 *      lead; the Documents tab on the full profile owns the full list).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  X, Copy, Phone as PhoneIcon, Mail as MailIcon, MapPin, FileText,
  Pencil, ExternalLink, CalendarClock, Flame,
  BadgeCheck, Ban, Timer,
} from 'lucide-react';
import { Button } from '../ui/button';
import { formatPhone } from '../../lib/utils';
import { TeamStore } from '../../lib/teamStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Loan-detail pairs — rendered as a single-column vertical stack so
// each field has room to breathe. Only populated keys render; missing
// values (null, blank string, or a literal 0 on monetary / rate fields
// — which never means "zero" in mortgage lending) show "—" so the LO
// sees what Grok/Arive hasn't captured yet. Pickers return `null` for
// any "unset" shape; the stringifier below renders the em-dash.
const nz = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const LOAN_FIELDS = [
  ['Loan Purpose',       (l) => (l.application?.loanPurpose && String(l.application.loanPurpose).trim()) || l.loanIntent || null],
  ['Loan Type',          (l) => (l.application?.loanType && String(l.application.loanType).trim()) || null],
  ['Loan Amount',        (l) => {
    const amt = nz(l.estAmount) ?? nz(l.application?.loanAmount) ?? nz(l.application?.purchasePrice);
    return amt ? fmtCurrency(amt) : null;
  }],
  ['Property Value',     (l) => nz(l.application?.propertyValue) ? fmtCurrency(l.application.propertyValue) : null],
  ['Current Rate',       (l) => {
    const r = nz(l.application?.rate) ?? nz(l.application?.loanRate);
    return r != null ? `${r.toFixed(3)}%` : null;
  }],
  ['LTV',                (l) => nz(l.application?.ltv) != null ? `${Number(l.application.ltv).toFixed(1)}%` : null],
  ['Current Payment',    (l) => nz(l.application?.currentPayment) ? fmtCurrency(l.application.currentPayment) : null],
  ['Est. New Payment',   (l) => nz(l.application?.estPayment) ? fmtCurrency(l.application.estPayment) : null],
  ['Occupancy',          (l) => (l.application?.occupancy && String(l.application.occupancy).trim()) || null],
  ['Property Type',      (l) => (l.application?.propertyType && String(l.application.propertyType).trim()) || null],
  ['Credit Score',       (l) => nz(l.application?.creditScore) || null],
];

const STATUS_META = {
  hot:          { label: 'Hot',          cls: 'bg-red-50 text-red-700 border-red-100',          icon: Flame },
  pre_approved: { label: 'Pre-Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: BadgeCheck },
  warm:         { label: 'Warm',         cls: 'bg-amber-50 text-amber-700 border-amber-100',    icon: null },
  cold:         { label: 'Cold',         cls: 'bg-sky-50 text-sky-700 border-sky-100',          icon: null },
  nurture:      { label: 'Nurture',      cls: 'bg-neutral-100 text-neutral-700 border-neutral-200', icon: null },
  dnc:          { label: 'DNC',          cls: 'bg-neutral-900 text-white border-neutral-900',   icon: Ban },
  strike_rate:  { label: 'Strike Rate',  cls: 'bg-indigo-50 text-indigo-700 border-indigo-100', icon: null },
  second_voice: { label: '2nd Voice',    cls: 'bg-sky-50 text-sky-700 border-sky-100',          icon: null },
};

function fmtCurrency(n) {
  if (n == null || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return null;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function initialsOf(name) {
  if (!name) return '??';
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function fmtPhx(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Phoenix is Arizona-based (America/Phoenix, no DST). Match the dialer
  // pin so this panel reads consistently with the Outbound workspace.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Phoenix',
  }).format(d);
}

function SectionCard({ title, action, children, testId }) {
  return (
    <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 px-3.5 py-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatusPill({ status, apptToday }) {
  const meta = STATUS_META[status] || null;
  if (apptToday) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40">
        <CalendarClock className="h-3 w-3" />
        Today
      </span>
    );
  }
  if (!meta) {
    return <span className="text-[11px] text-neutral-400">No status</span>;
  }
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${meta.cls}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {meta.label}
    </span>
  );
}

export default function LeadDetailPanel({ lead, me, onClose }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  // Inline Add Note editor — opens inside the Recent Notes section so
  // the LO stays in the right rail (no modal, no page nav). State is
  // keyed to the LO's draft text so an in-flight save failure keeps
  // their words. Saving goes through the SAME `/api/notes` endpoint
  // the Lead Profile notes tab uses — zero new backend wiring.
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const noteFieldRef = useRef(null);

  // Fetch recent notes whenever the panel focuses a new lead. We cap
  // the list to the three most recent — the full history lives on the
  // existing /leads/:id profile route.
  useEffect(() => {
    if (!lead?.id) { setNotes([]); return; }
    let cancelled = false;
    setLoadingNotes(true);
    axios.get(`${API}/notes`, { params: { leadId: lead.id } })
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        // Backend already returns newest-first (per notes.py).
        setNotes(list.slice(0, 3));
      })
      .catch(() => { if (!cancelled) setNotes([]); })
      .finally(() => { if (!cancelled) setLoadingNotes(false); });
    return () => { cancelled = true; };
  }, [lead?.id]);

  // Reset the inline editor when the user picks a different borrower
  // (prevents a half-typed note from leaking across leads).
  useEffect(() => {
    setNoteOpen(false);
    setNoteDraft('');
    setSavingNote(false);
  }, [lead?.id]);

  // Autofocus the textarea when the inline editor opens.
  useEffect(() => {
    if (noteOpen && noteFieldRef.current) {
      noteFieldRef.current.focus();
    }
  }, [noteOpen]);

  if (!lead) {
    return (
      <aside
        className="hidden xl:flex xl:w-[420px] shrink-0 border-l hairline bg-white dark:bg-neutral-950 flex-col items-center justify-center text-neutral-400 text-sm xl:z-10 xl:sticky xl:top-16 xl:self-start xl:h-[calc(100vh-4rem)] xl:-mt-8 xl:-mb-8"
        data-testid="lead-detail-panel-empty"
      >
        <FileText className="h-6 w-6 mb-2 opacity-50" />
        Select a borrower to see their summary
      </aside>
    );
  }

  const digits = (lead.phone || '').replace(/\D/g, '');
  // Appointment today? Drives the gold "Today" pill in the status
  // chip — matches the row-level behavior.
  const apptToday = (() => {
    if (!lead.appointmentAt) return false;
    const d = new Date(lead.appointmentAt);
    if (Number.isNaN(d.getTime())) return false;
    return d.toDateString() === new Date().toDateString();
  })();
  const assignee = lead.assignedTo ? TeamStore.get(lead.assignedTo) : null;
  // "Last activity" = max of lastContact / lastCallAt / updatedAt as a
  // conservative proxy. Falls back to just "updatedAt" since every lead
  // doc carries one.
  const lastActivityIso = lead.lastContactAt
    || lead.outboundLastCallAt
    || lead.updatedAt
    || lead.createdAt;

  function handleCopy(text) {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Could not copy'),
    );
  }

  function handleOpenProfile() {
    navigate(`/leads/${lead.id}`);
  }

  function handleAddNote() {
    // Open the inline editor. We prefer this over a modal because the
    // right-side panel is already a dedicated workspace — opening a
    // modal on top would cover the Call Borrower button + loan details
    // the LO is reviewing. Matches the Dancing Shoes pattern.
    setNoteOpen(true);
  }

  async function handleSaveNote() {
    const body = noteDraft.trim();
    if (!body || savingNote) return;
    setSavingNote(true);
    try {
      const r = await axios.post(`${API}/notes`, { leadId: lead.id, body });
      // Prepend the new note so the LO sees it immediately. Backend
      // already stamps author/authorId from the JWT, so we trust the
      // echoed doc as the source of truth for the Recent Notes list.
      setNotes((prev) => [r.data, ...prev].slice(0, 3));
      setNoteDraft('');
      setNoteOpen(false);
      toast.success('Note saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save note. Try again.');
      // Keep the draft intact so the LO doesn't lose what they typed.
    } finally {
      setSavingNote(false);
    }
  }

  function handleCancelNote() {
    setNoteDraft('');
    setNoteOpen(false);
  }

  const detailPairs = LOAN_FIELDS.map(([label, picker]) => {
    const raw = picker(lead);
    return [label, raw == null || raw === '' ? '—' : String(raw)];
  });

  return (
    <aside
      // On mobile this is a full-height overlay (fixed inset-y-0). On
      // xl desktops it becomes a *sticky* right-rail: anchored just
      // below the global TopBar (sticky `top-16` matches TopBar's
      // `h-16`), owning its own scroll area so the lead list can scroll
      // under it indefinitely without the borrower summary going
      // off-screen. Negative top/bottom margins pull the aside through
      // the main's `py-8` padding (at xl the sm:py-8 is already active)
      // so the panel's top edge lines up flush with the bottom of the
      // TopBar — no empty gap above the borrower header.
      //
      // Internal structure (Feb 10, 2026 — "sticky header" pass):
      //   • Fixed header   — avatar + name + status + close
      //   • Fixed actions  — Call Borrower + Add Note + Open full profile
      //   • Scrollable     — Contact / Loan Details / Notes / Follow-ups / Docs
      // Only the bottom section scrolls, so the LO never loses the
      // "Call Borrower" CTA while reviewing details.
      className="fixed inset-y-0 right-0 z-30 w-full sm:w-[460px] shrink-0 border-l hairline bg-neutral-50/40 dark:bg-neutral-950 flex flex-col shadow-xl xl:w-[420px] xl:z-10 xl:shadow-none xl:sticky xl:top-16 xl:self-start xl:h-[calc(100vh-4rem)] xl:-mt-8 xl:-mb-8"
      data-testid="lead-detail-panel"
    >
      {/* Header — fixed at the top of the panel */}
      <div className="px-4 pt-4 pb-3 bg-white dark:bg-neutral-950 border-b hairline flex items-start gap-3 shrink-0">
        <div className="h-10 w-10 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[12px] font-semibold shrink-0">
          {initialsOf(lead.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleOpenProfile}
              className="text-[16px] font-semibold text-neutral-900 dark:text-white truncate hover:underline underline-offset-2"
              data-testid="lead-detail-name-link"
              title="Open full profile"
            >
              {lead.name}
            </button>
            <StatusPill status={lead.temp} apptToday={apptToday} />
          </div>
          <div className="text-[11.5px] text-neutral-500 mt-0.5">
            {assignee ? `Assigned to ${assignee.name}` : 'Unassigned'}
            {lastActivityIso && (() => {
              const ago = timeAgo(lastActivityIso);
              return ago ? ` • Last activity ${ago}` : '';
            })()}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-neutral-400 hover:text-neutral-900 p-1 shrink-0"
          data-testid="lead-detail-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Primary actions — fixed. Call (black / gold-in-dark) + Add Note
          (outline). The 3-dot overflow menu was removed Feb 2026;
          "Open full profile" below carries the overflow role. */}
      <div className="px-4 pt-3 pb-4 bg-white dark:bg-neutral-950 border-b hairline space-y-2 shrink-0">
        <div className="flex items-center gap-2">
          <a
            href={digits ? `tel:${digits}` : undefined}
            onClick={(e) => { if (!digits) { e.preventDefault(); toast.error('No phone on this lead.'); } }}
            className={`flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-lg text-[13px] font-semibold transition-colors ${
              digits
                ? 'bg-neutral-900 hover:bg-black text-white dark:bg-[hsl(var(--kf-gold))] dark:hover:bg-[hsl(42_82%_64%)] dark:text-neutral-900 dark:shadow-[0_1px_0_rgba(0,0,0,0.3)]'
                : 'bg-neutral-200 text-neutral-500 cursor-not-allowed dark:bg-neutral-800 dark:text-neutral-600'
            }`}
            data-testid="lead-detail-call-btn"
          >
            <PhoneIcon className="h-4 w-4" />
            Call Borrower
          </a>
          <button
            onClick={handleAddNote}
            className="h-10 inline-flex items-center gap-1.5 px-3.5 rounded-lg border hairline bg-white dark:bg-neutral-950 text-[12.5px] font-medium text-neutral-800 hover:bg-neutral-50"
            data-testid="lead-detail-add-note"
          >
            <Pencil className="h-3.5 w-3.5" />
            Add Note
          </button>
        </div>
        <Button
          onClick={handleOpenProfile}
          variant="outline"
          className="w-full h-8 text-[12px] font-medium"
          data-testid="lead-detail-open-profile"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          Open full profile
        </Button>
      </div>

      {/* Scroll surface — only the detail sections scroll. The header
          and action buttons above stay anchored so the LO never loses
          the Call CTA while reading loan details. */}
      <div className="flex-1 overflow-y-auto min-h-0" data-testid="lead-detail-scroll">
        <div className="px-4 py-4 space-y-3">
          {/* Contact Information */}
          <SectionCard title="Contact Information" testId="lead-detail-contact">
            <div className="space-y-2">
              {lead.phone ? (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <PhoneIcon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <a href={`tel:${digits}`} className="tabular-nums hover:text-neutral-900">{formatPhone(lead.phone)}</a>
                  <button onClick={() => handleCopy(lead.phone)} className="text-neutral-300 hover:text-neutral-700 ml-auto" aria-label="Copy phone">
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-400">
                  <PhoneIcon className="h-3.5 w-3.5 shrink-0" />
                  No phone
                </div>
              )}
              {lead.email ? (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <MailIcon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <a href={`mailto:${lead.email}`} className="truncate hover:text-neutral-900">{lead.email}</a>
                  <button onClick={() => handleCopy(lead.email)} className="text-neutral-300 hover:text-neutral-700 ml-auto" aria-label="Copy email">
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-400">
                  <MailIcon className="h-3.5 w-3.5 shrink-0" />
                  No email
                </div>
              )}
              {(lead.application?.propertyAddress || lead.city) ? (
                <div className="flex items-start gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <MapPin className="h-3.5 w-3.5 text-neutral-400 shrink-0 mt-0.5" />
                  <span className="truncate flex-1">
                    {lead.application?.propertyAddress ? `${lead.application.propertyAddress}, ` : ''}
                    {[lead.application?.propertyCity || lead.city, lead.application?.propertyState, lead.application?.propertyZip]
                      .filter(Boolean).join(' ')}
                  </span>
                  <button
                    onClick={() => handleCopy([lead.application?.propertyAddress, lead.application?.propertyCity, lead.application?.propertyState, lead.application?.propertyZip].filter(Boolean).join(', '))}
                    className="text-neutral-300 hover:text-neutral-700" aria-label="Copy address"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-400">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  No property on file
                </div>
              )}
            </div>
          </SectionCard>

          {/* Loan Details */}
          <SectionCard
            title="Loan Details"
            action={
              <button
                onClick={handleOpenProfile}
                className="inline-flex items-center gap-1 text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            }
            testId="lead-detail-loan"
          >
            <div className="grid grid-cols-2 gap-y-2.5 gap-x-4">
              {detailPairs.map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <div className="text-[10px] text-neutral-400 uppercase tracking-wider">{label}</div>
                  <div className="text-[12.5px] text-neutral-900 dark:text-neutral-100 font-semibold truncate mt-0.5">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Recent Notes — placed directly under Loan Details per the
              Feb 2026 refinement. Hosts the inline Add Note editor so
              the "Add Note" button in the primary actions row never
              takes the LO off this panel. */}
          <SectionCard
            title="Recent Notes"
            action={
              <div className="flex items-center gap-3">
                {!noteOpen && (
                  <button
                    onClick={handleAddNote}
                    className="text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
                    data-testid="lead-detail-notes-add-trigger"
                  >
                    Add Note
                  </button>
                )}
                <button
                  onClick={handleOpenProfile}
                  className="text-[11.5px] text-neutral-500 hover:text-neutral-900 font-medium"
                >
                  View All
                </button>
              </div>
            }
            testId="lead-detail-notes"
          >
            {noteOpen && (
              <div
                className="mb-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-2.5"
                data-testid="lead-detail-note-editor"
              >
                <div className="text-[11px] text-neutral-500 mb-1.5">
                  Add Note — <span className="font-semibold text-neutral-800 dark:text-neutral-200">{lead.name}</span>
                </div>
                <textarea
                  ref={noteFieldRef}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd/Ctrl+Enter to save, Esc to cancel — standard
                    // keyboard contract for inline composers.
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveNote();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelNote();
                    }
                  }}
                  placeholder="Write a note about this borrower…"
                  rows={3}
                  className="w-full text-[12.5px] text-neutral-800 dark:text-neutral-200 bg-transparent border-0 focus:ring-0 focus:outline-none resize-none placeholder:text-neutral-400"
                  data-testid="lead-detail-note-input"
                />
                <div className="flex items-center justify-end gap-2 mt-1.5">
                  <button
                    onClick={handleCancelNote}
                    disabled={savingNote}
                    className="h-7 px-3 rounded-md text-[11.5px] font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
                    data-testid="lead-detail-note-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNote}
                    disabled={savingNote || !noteDraft.trim()}
                    className="h-7 px-3 rounded-md text-[11.5px] font-semibold bg-neutral-900 hover:bg-black text-white disabled:bg-neutral-300 disabled:cursor-not-allowed"
                    data-testid="lead-detail-note-save"
                  >
                    {savingNote ? 'Saving…' : 'Save Note'}
                  </button>
                </div>
              </div>
            )}
            {loadingNotes && (
              <div className="text-[11.5px] text-neutral-400 italic text-center py-3">Loading notes…</div>
            )}
            {!loadingNotes && notes.length === 0 && !noteOpen && (
              <div className="text-[11.5px] text-neutral-400 italic text-center py-3">No notes yet.</div>
            )}
            {!loadingNotes && notes.length > 0 && (
              <div className="space-y-2.5">
                {notes.map((n) => (
                  <div key={n.id || n._id || n.createdAt} className="rounded-lg bg-neutral-50 dark:bg-neutral-900 px-3 py-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[11.5px] font-semibold text-neutral-700 dark:text-neutral-300 truncate">
                        {n.authorName || n.author || 'Unknown'}
                      </div>
                      <div className="text-[10.5px] text-neutral-400 shrink-0">{timeAgo(n.createdAt) || ''}</div>
                    </div>
                    <div className="text-[12.5px] text-neutral-800 dark:text-neutral-200 leading-snug whitespace-pre-wrap line-clamp-3">
                      {n.body}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Follow-ups & Appointments */}
          <SectionCard title="Follow-ups & Appointments" testId="lead-detail-schedule">
            <div className="space-y-2 text-[12.5px]">
              <ScheduleRow
                icon={CalendarClock}
                label="Upcoming Appointment"
                value={lead.appointmentAt ? fmtPhx(lead.appointmentAt) : null}
                emphasize={apptToday}
              />
              <ScheduleRow
                icon={Timer}
                label="Next Follow-Up"
                value={lead.followUpAt ? fmtPhx(lead.followUpAt) : null}
              />
              <ScheduleRow
                icon={PhoneIcon}
                label="Last Contact"
                value={lead.lastContactAt ? fmtPhx(lead.lastContactAt)
                  : lead.outboundLastCallAt ? fmtPhx(lead.outboundLastCallAt) : null}
              />
            </div>
          </SectionCard>

          {/* Documents — full width, matching the Grok Leads layout */}
          <SectionCard
            title="Documents"
            action={
              <button
                onClick={handleOpenProfile}
                className="text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
              >
                View All
              </button>
            }
            testId="lead-detail-docs"
          >
            <div className="text-[11.5px] text-neutral-400 italic text-center py-4">
              Open the full profile to review this borrower&apos;s documents.
            </div>
          </SectionCard>
        </div>
      </div>
    </aside>
  );
}

function ScheduleRow({ icon: Icon, label, value, emphasize = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${emphasize ? 'text-[hsl(var(--kf-gold-deep))]' : 'text-neutral-400'}`} />
      <div className="text-neutral-500 flex-1">{label}</div>
      <div className={`tabular-nums ${emphasize ? 'text-[hsl(var(--kf-gold-deep))] font-semibold' : (value ? 'text-neutral-900 dark:text-neutral-100 font-medium' : 'text-neutral-400')}`}>
        {value || '—'}
      </div>
    </div>
  );
}
